import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { trpc } from "../../lib/trpc";
import { ProductionScopePicker } from "./ProductionScopePicker";

interface ExistingUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  tesisId?: string | null;
  roles: Array<{ id: string; code: string; name: string }>;
  assignedLocationIds?: string[];
}

interface UserFormModalProps {
  open: boolean;
  initial?: ExistingUser | null;
  onClose: () => void;
}

interface FormState {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  tesisId: string;
  roleIds: string[];
  assignedLocationIds: string[];
}

const EMPTY: FormState = { email: "", firstName: "", lastName: "", password: "", tesisId: "", roleIds: [], assignedLocationIds: [] };

export function UserFormModal({ open, initial, onClose }: UserFormModalProps) {
  const isEdit = !!initial;
  const [state, setState] = useState<FormState>(EMPTY);
  const [err, setErr] = useState<string | null>(null);
  const qc = useQueryClient();

  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const isSuper = !!(meQ.data?.user as { isSuperAdmin?: boolean } | undefined)?.isSuperAdmin;
  const facilities = (meQ.data?.user as { facilities?: Array<{ id: string; code: string; name: string }> } | undefined)?.facilities ?? [];
  const activeFacilityId = (meQ.data?.user as { activeFacilityId?: string | null } | undefined)?.activeFacilityId ?? null;
  // Tesis seçici yalnız süper-admin + birden çok tesis varken; normal yönetici için sunucu kendi tesisine sabitler.
  const showTesis = isSuper && facilities.length > 1;

  useEffect(() => {
    if (open) {
      setState(initial ? {
        email: initial.email,
        firstName: initial.firstName,
        lastName: initial.lastName,
        password: "",
        tesisId: initial.tesisId ?? "",
        roleIds: initial.roles.map((r) => r.id),
        assignedLocationIds: initial.assignedLocationIds ?? [],
      } : { ...EMPTY, tesisId: activeFacilityId ?? facilities[0]?.id ?? "" });
      setErr(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const rolesQ = trpc.role.list.useQuery(undefined, { staleTime: 60_000 });
  // Merkez-atama ağacı: kullanıcının tesisinin üretim merkezleri + hatları (süper-admin başka tesisi de çeker).
  const centersQ = trpc.location.centersForFacility.useQuery({ tesisId: state.tesisId || null }, { enabled: open });
  const createMut = trpc.user.create.useMutation();
  const updateMut = trpc.user.update.useMutation();
  const isPending = createMut.isPending || updateMut.isPending;

  const errorMessage = useMemo(() => {
    const mut = createMut.error ?? updateMut.error;
    if (err) return err;
    if (!mut) return null;
    const zod = (mut.data as { zodError?: { fieldErrors?: Record<string, string[]> } } | undefined)?.zodError?.fieldErrors;
    if (zod) return Object.values(zod).flat().filter(Boolean).join(" · ");
    return mut.message;
  }, [err, createMut.error, updateMut.error]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((s) => ({ ...s, [key]: value }));
  }

  function toggleRole(roleId: string) {
    setState((s) => ({
      ...s,
      roleIds: s.roleIds.includes(roleId) ? s.roleIds.filter((r) => r !== roleId) : [...s.roleIds, roleId],
    }));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    if (!state.email.trim()) return setErr("E-posta zorunlu.");
    if (!state.firstName.trim() || !state.lastName.trim()) return setErr("Ad ve soyad zorunlu.");
    if (state.roleIds.length === 0) return setErr("En az 1 rol seçin.");
    if (!isEdit && state.password.length < 8) return setErr("Parola en az 8 karakter olmalı.");

    try {
      if (isEdit && initial) {
        await updateMut.mutateAsync({
          id: initial.id,
          firstName: state.firstName,
          lastName: state.lastName,
          tesisId: showTesis ? (state.tesisId || null) : undefined,
          roleIds: state.roleIds,
          assignedLocationIds: state.assignedLocationIds,
        });
      } else {
        await createMut.mutateAsync({
          email: state.email,
          password: state.password,
          firstName: state.firstName,
          lastName: state.lastName,
          tesisId: showTesis ? (state.tesisId || null) : undefined,
          roleIds: state.roleIds,
          assignedLocationIds: state.assignedLocationIds,
        });
      }
      await qc.invalidateQueries();
      onClose();
    } catch {
      /* hata banner'da */
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}
      subtitle={isEdit ? initial!.email : "Tüm zorunlu alanları doldurun (*)"}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={isPending}>İptal</button>
          <button type="submit" form="user-form" className="btn-md primary" disabled={isPending}>
            {isPending ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Oluştur"}
          </button>
        </>
      }
    >
      <form id="user-form" onSubmit={handleSubmit} noValidate>
        {errorMessage ? <div className="form-banner-err">{errorMessage}</div> : null}

        <div className="form-field">
          <label className="lbl" htmlFor="uf-email">E-posta <span className="req">*</span></label>
          <input
            id="uf-email"
            className="form-input"
            type="email"
            value={state.email}
            onChange={(e) => setField("email", e.target.value)}
            autoComplete="email"
            disabled={isEdit}
            required
          />
          <span className="help">Yarat sonrası değiştirilemez.</span>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="uf-fn">Ad <span className="req">*</span></label>
            <input
              id="uf-fn"
              className="form-input"
              type="text"
              value={state.firstName}
              onChange={(e) => setField("firstName", e.target.value)}
              maxLength={100}
              required
            />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="uf-ln">Soyad <span className="req">*</span></label>
            <input
              id="uf-ln"
              className="form-input"
              type="text"
              value={state.lastName}
              onChange={(e) => setField("lastName", e.target.value)}
              maxLength={100}
              required
            />
          </div>
        </div>

        {!isEdit ? (
          <div className="form-field">
            <label className="lbl" htmlFor="uf-pw">Parola <span className="req">*</span></label>
            <input
              id="uf-pw"
              className="form-input"
              type="password"
              value={state.password}
              onChange={(e) => setField("password", e.target.value)}
              minLength={8}
              autoComplete="new-password"
              required
            />
            <span className="help">En az 8 karakter. Kullanıcı sonrasında değiştirebilir.</span>
          </div>
        ) : null}

        {showTesis ? (
          <div className="form-field">
            <label className="lbl" htmlFor="uf-tesis">Tesis <span className="req">*</span></label>
            <select id="uf-tesis" className="form-select" value={state.tesisId} onChange={(e) => setField("tesisId", e.target.value)}>
              <option value="">— Global (tüm tesisler) —</option>
              {facilities.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.code})</option>)}
            </select>
            <span className="help">Kullanıcının ait olduğu tesis. "Global" yalnız sistem yöneticileri içindir.</span>
          </div>
        ) : null}

        <div className="form-field">
          <span className="lbl">Roller <span className="req">*</span></span>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {(rolesQ.data ?? []).map((r) => (
              <label key={r.id} className="form-checkbox" style={{
                padding: "8px 10px",
                border: `1px solid ${state.roleIds.includes(r.id) ? "var(--primary)" : "var(--neutral-200)"}`,
                background: state.roleIds.includes(r.id) ? "var(--primary-subtle)" : "var(--white)",
                borderRadius: "var(--radius-md)",
              }}>
                <input
                  type="checkbox"
                  checked={state.roleIds.includes(r.id)}
                  onChange={() => toggleRole(r.id)}
                />
                <span>
                  <span style={{ fontWeight: 500 }}>{r.name}</span>
                  <span style={{ display: "block", fontSize: 11, color: "var(--neutral-500)" }}>{r.description ?? r.code}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-field">
          <span className="lbl">Yetkili Üretim Merkezleri / Hatları</span>
          <ProductionScopePicker
            locations={centersQ.data ?? []}
            selectedIds={state.assignedLocationIds}
            onChange={(ids) => setState((s) => ({ ...s, assignedLocationIds: ids }))}
          />
          <span className="help">Boş bırakılırsa kullanıcı tesisin TÜM üretimini görür. Merkez/hat seçilirse üretim, iş emirleri, siparişler ve kalite onayları yalnız seçili merkez(ler)e sınırlanır.</span>
        </div>
      </form>
    </Drawer>
  );
}
